import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, products } from '@/mocks/products';

export default function CategoryPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const filteredProducts =
    activeCategory === 'all'
      ? products
      : products.filter((p) => p.category === activeCategory);

  const activeCategoryName = categories.find((c) => c.id === activeCategory)?.name || '全部';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-18">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '产品分类' },
            ]}
          />

          <section className="pb-14 md:pb-20">
            <div className="text-center mb-8 md:mb-10">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-3">
                产品分类
              </h1>
              <p className="text-sm text-foreground-600 max-w-md mx-auto">
                探索嫩小兔的完整产品线，找到属于你的萌趣好物
              </p>
            </div>

            <div className="flex justify-center mb-10">
              <div className="inline-flex bg-background-100 rounded-full p-1 flex-wrap justify-center gap-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-background-50 text-primary-600'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6" data-product-shop>
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300"
                >
                  <div className="relative aspect-square overflow-hidden bg-background-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - 嫩小兔萌系潮流玩具`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      {product.isNew && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                          新品
                        </span>
                      )}
                      {product.isHot && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                          热卖
                        </span>
                      )}
                      {product.isLimited && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-200 text-secondary-800 whitespace-nowrap">
                          限定
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {product.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-md text-xs bg-background-100 text-foreground-500 whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {product.description}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-label text-base md:text-lg font-bold text-primary-500">
                        {product.price}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无产品</p>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `嫩小兔产品分类 - ${activeCategoryName}`,
            description: `嫩小兔${activeCategoryName}产品展示，萌系潮流玩具，汇聚玩偶、周边、联名与礼盒。`,
            url: 'https://www.nenxiaotu.com/category',
            isPartOf: {
              '@type': 'WebSite',
              name: '嫩小兔',
              url: 'https://www.nenxiaotu.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.nenxiaotu.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '产品分类',
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}